import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import DashboardLayout from '../../components/layout/DashboardLayout';
import Card from '../../components/ui/Card';
import Button from '../../components/ui/Button';
import Badge from '../../components/ui/Badge';
import StatCard from '../../components/ui/StatCard';
import GradientIcon from '../../components/ui/GradientIcon';
import { LayoutGrid, PlayCircle, Calendar, CheckCircle2, Clock, CalendarClock } from 'lucide-react';
import Modal from '../../components/ui/Modal';
import RescheduleSessionModal from '../../components/session/RescheduleSessionModal';
import Pagination from '../../components/ui/Pagination';
import { SessionListSkeleton, DashboardStatsSkeleton } from '../../components/ui/LoadingSkeleton';
import { NoSessionsEmpty, NoResultsEmpty } from '../../components/ui/EmptyState';
import { notifications, showSuccess } from '../../utils/toast';
import { useKeyboardShortcut } from '../../hooks/useKeyboardShortcuts';
import { sessionService, clientService } from '../../services';
import ResponsiveDataTable from '../../components/ui/ResponsiveDataTable';
import exportService from '../../services/exportService';
import toast from 'react-hot-toast';
import { usePreferences } from '../../contexts/PreferencesContext';

/**
 * Sessions List Page - Client View
 * Shows all sessions with filtering and actions
 */

const SessionsPage = () => {
  const navigate = useNavigate();
  const { formatDateWithTimezone, formatTime } = usePreferences();
  const [organization, setOrganization] = useState(null);
  const [sessions, setSessions] = useState([]);
  // Server-reported session usage for the current billing period — same
  // source as the Subscription page's Usage Statistics card. Counting the
  // local `sessions` array client-side double-counts sessions from previous
  // billing periods after a renewal/upgrade/extension, which is why the
  // limit banner kept firing even when the period had reset.
  const [sessionUsage, setSessionUsage] = useState(null); // { used, limit }
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all'); // all, live, scheduled, completed, expired, archived
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [rescheduleModal, setRescheduleModal] = useState({ isOpen: false, session: null });
  const [sessionToDelete, setSessionToDelete] = useState(null);
  const [showArchived, setShowArchived] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [searchQuery, setSearchQuery] = useState('');
  const [timePeriod, setTimePeriod] = useState('all'); // all, today, week, month
  const [itemsPerPage, setItemsPerPage] = useState(10);

  // Helper function to calculate effective status based on time.
  // A live session stays live until the trainer ends it — sessions routinely
  // overrun their planned end time and must not be shown as expired.
  // Only a session that was scheduled and never started expires once its window passes.
  const getEffectiveStatus = (session) => {
    if (session.status !== 'scheduled') {
      return session.status;
    }

    const now = new Date();
    const endTime = session.endTime ? new Date(session.endTime) : null;

    if (endTime && !isNaN(endTime.getTime()) && endTime < now) {
      return 'expired';
    }

    return session.status;
  };

  useEffect(() => {
    fetchSessionsData();
  }, []);

  const fetchSessionsData = async () => {
    try {
      setLoading(true);

      // Get user from localStorage for organization info
      const userData = localStorage.getItem('user');
      if (userData) {
        const parsedUser = JSON.parse(userData);
        if (parsedUser.organization) {
          setOrganization({
            id: parsedUser.organization._id || parsedUser.organization,
            subscriptionTier: parsedUser.organization.plan || parsedUser.organization.subscriptionTier || 'free',
          });
        }
      }

      // Fetch sessions + authoritative billing-period usage in parallel.
      // Backend tracks usage by `currentPeriodStart` and resets the count
      // on plan renewal / upgrade / extension, so this number stays correct
      // across plan changes (matches /client/subscription Usage Statistics).
      const [response, subResp] = await Promise.all([
        sessionService.getSessions({ limit: 1000 }),
        clientService.getSubscription().catch(() => null),
      ]);

      if (subResp?.success) {
        const apiUsage = subResp.data?.usage?.sessions;
        const apiFeatures = subResp.data?.features || subResp.data?.subscription?.features;
        if (apiUsage) {
          setSessionUsage({
            used: apiUsage.used ?? 0,
            limit: apiUsage.limit ?? apiFeatures?.sessions ?? null,
          });
        }
      }

      if (response.success) {
        // Transform API response to match component format
        const transformedSessions = (response.data.sessions || response.data || []).map((session) => {
          const baseSession = {
            id: session._id,
            name: session.name,
            code: session.code,
            status: session.status || 'scheduled',
            trainerName: session.trainer?.firstName
              ? `${session.trainer.firstName} ${session.trainer.lastName || ''}`
              : 'Unknown Trainer',
            trainerId: session.trainer?._id || session.trainer,
            scheduledDate: session.scheduledDate,
            startTime: session.startTime,
            endTime: session.endTime,
            currentParticipants: session.currentParticipants || session.participants?.length || 0,
            expectedParticipants: session.expectedParticipants || session.maxParticipants || 30,
            maxParticipants: session.maxParticipants || 50,
            subSessions: session.subSessions || [],
            workbooks: session.workbooks || [],
            createdAt: session.createdAt,
          };
          // Apply effective status based on time
          baseSession.status = getEffectiveStatus(baseSession);
          return baseSession;
        });
        setSessions(transformedSessions);
      }
    } catch (error) {
      console.error('Error fetching sessions:', error);
    } finally {
      setLoading(false);
    }
  };

  // Keyboard shortcuts
  useKeyboardShortcut(['ctrl+n', 'cmd+n'], () => {
    if (canCreateSession() && !showDeleteModal) {
      handleCreateSession();
    }
  });

  useKeyboardShortcut('1', () => {
    if (!showDeleteModal) setFilter('all');
  });

  useKeyboardShortcut('2', () => {
    if (!showDeleteModal) setFilter('live');
  });

  useKeyboardShortcut('3', () => {
    if (!showDeleteModal) setFilter('scheduled');
  });

  useKeyboardShortcut('4', () => {
    if (!showDeleteModal) setFilter('completed');
  });

  useKeyboardShortcut(['escape', 'esc'], () => {
    if (showDeleteModal) {
      setShowDeleteModal(false);
      setSessionToDelete(null);
    }
  });

  // Filter sessions
  const filteredSessions = sessions
    .filter((session) => {
      // Filter out archived by default unless showing archived
      if (!showArchived && session.status === 'archived') return false;

      // Search filter
      if (searchQuery) {
        const query = searchQuery.toLowerCase();
        const nameMatch = session.name?.toLowerCase().includes(query);
        const codeMatch = session.code?.toLowerCase().includes(query);
        const trainerMatch = session.trainerName?.toLowerCase().includes(query);
        if (!nameMatch && !codeMatch && !trainerMatch) return false;
      }

      // Time period filter
      if (timePeriod !== 'all') {
        const sessionDate = new Date(session.startTime || session.createdAt);
        const now = new Date();
        const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
        const weekAgo = new Date(today.getTime() - 7 * 24 * 60 * 60 * 1000);
        const monthAgo = new Date(today.getTime() - 30 * 24 * 60 * 60 * 1000);

        if (timePeriod === 'today' && sessionDate < today) return false;
        if (timePeriod === 'week' && sessionDate < weekAgo) return false;
        if (timePeriod === 'month' && sessionDate < monthAgo) return false;
      }

      // Status filter
      if (filter === 'all') return true;
      if (filter === 'live') return session.status === 'live';
      if (filter === 'scheduled') return session.status === 'scheduled';
      if (filter === 'completed') return session.status === 'completed';
      if (filter === 'expired') return session.status === 'expired';
      if (filter === 'archived') return session.status === 'archived';
      return true;
    })
    // Sort by date descending (newest first) by default
    .sort((a, b) => new Date(b.startTime || b.createdAt) - new Date(a.startTime || a.createdAt));

  // Paginate filtered sessions
  const totalPages = Math.ceil(filteredSessions.length / itemsPerPage);
  const paginatedSessions = filteredSessions.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  // Reset to page 1 when filter/search/time/itemsPerPage changes
  useEffect(() => {
    setCurrentPage(1);
  }, [filter, showArchived, searchQuery, timePeriod, itemsPerPage]);

  // Check if can create new session — uses the server-side billing-period
  // usage so plan renewal / upgrade / extension resets the counter
  // correctly. Falls back to optimistic-allow during the initial paint
  // before the subscription endpoint resolves; the banner appears as soon
  // as usage data arrives. Backend limits: free=3 total, pro=20/month,
  // pro_plus/enterprise=unlimited.
  const canCreateSession = () => {
    if (sessionUsage) {
      const { used, limit } = sessionUsage;
      // -1 / null / undefined on the limit means unlimited (Pro Plus / Enterprise).
      if (limit === -1 || limit === null || limit === undefined) return true;
      return (used || 0) < limit;
    }
    // Fallback while subscription data is still loading — be optimistic.
    const tier = organization?.subscriptionTier || 'free';
    if (['pro_plus', 'enterprise_starter', 'enterprise_scale'].includes(tier)) return true;
    return true;
  };

  const handleCreateSession = () => {
    if (!canCreateSession()) {
      alert('Session limit reached. Close existing sessions or upgrade your plan.');
      return;
    }
    navigate('/client/sessions/new');
  };

  const handleDeleteSession = (session) => {
    setSessionToDelete(session);
    setShowDeleteModal(true);
  };

  const handleDuplicateSession = async (session) => {
    try {
      // Use the duplicate endpoint which copies everything (subsessions, activities, workbooks)
      const response = await sessionService.duplicateSession(session.id || session._id);
      if (response.success) {
        const newSessionId = response.data?._id || response.data?.id;
        showSuccess(`"${session.name}" duplicated successfully! All subsessions, activities, and workbooks have been copied.`);
        // Navigate to edit page to allow changing date/time
        if (newSessionId) {
          navigate(`/client/sessions/${newSessionId}/edit`);
        } else {
          fetchSessionsData(); // Fallback: refresh the list
        }
      }
    } catch (error) {
      console.error('Error duplicating session:', error);
      toast.error(error.message || 'Failed to duplicate session');
    }
  };

  // Handle export sessions
  const handleExport = async () => {
    setIsExporting(true);
    try {
      const params = {};
      if (filter !== 'all' && filter !== 'archived') {
        params.status = filter;
      }
      await exportService.exportSessionsCSV(params);
      toast.success('Sessions exported successfully');
    } catch (error) {
      console.error('Export error:', error);
      toast.error(error.message || 'Failed to export sessions');
    } finally {
      setIsExporting(false);
    }
  };

  const generateSessionCode = () => {
    const letters = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ';
    const numbers = '0123456789';
    let code = '';
    for (let i = 0; i < 3; i++) code += letters.charAt(Math.floor(Math.random() * letters.length));
    for (let i = 0; i < 3; i++) code += numbers.charAt(Math.floor(Math.random() * numbers.length));
    return code;
  };

  const confirmDelete = async () => {
    try {
      const response = await sessionService.deleteSession(sessionToDelete.id);
      if (response.success) {
        setSessions(sessions.filter((s) => s.id !== sessionToDelete.id));
        notifications.sessionDeleted();
        setShowDeleteModal(false);
        setSessionToDelete(null);
      }
    } catch (error) {
      console.error('Error deleting session:', error);
      alert(error.response?.data?.message || 'Failed to delete session');
    }
  };

  // Get stats
  const stats = {
    total: sessions.filter((s) => s.status !== 'archived').length,
    live: sessions.filter((s) => s.status === 'live').length,
    scheduled: sessions.filter((s) => s.status === 'scheduled').length,
    completed: sessions.filter((s) => s.status === 'completed').length,
    expired: sessions.filter((s) => s.status === 'expired').length,
    archived: sessions.filter((s) => s.status === 'archived').length,
  };

  return (
    <DashboardLayout title="Sessions">
      <div className="p-4 space-y-4 pb-20 sm:pb-4">
        {/* Back Button */}
        <div className="mb-4">
          <button
            onClick={() => navigate('/client/dashboard')}
            className="inline-flex items-center gap-2 text-neutral-600 hover:text-neutral-900 transition-colors mb-2 group"
          >
            <svg className="w-4 h-4 group-hover:-translate-x-1 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
            <span className="text-xs sm:text-sm font-medium">Back to Dashboard</span>
          </button>
        </div>

        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 sm:gap-0">
          <div>
            <h1 className="text-xl sm:text-2xl font-bold text-neutral-900">Sessions</h1>
            <p className="text-xs sm:text-sm text-neutral-600 mt-1 hidden sm:block">Manage your training sessions</p>
          </div>
          <div className="flex items-center gap-2 sm:gap-3">
            <Button variant="ghost" size="md" onClick={handleExport} disabled={isExporting}>
              {isExporting ? (
                <svg className="animate-spin w-5 h-5" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                </svg>
              ) : (
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                </svg>
              )}
            </Button>
            <Button variant="primary" onClick={handleCreateSession} disabled={!canCreateSession()}>
              <svg className="w-5 h-5 sm:mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
              </svg>
              <span className="hidden sm:inline">Create Session</span>
            </Button>
          </div>
        </div>

        {/* Stats Cards */}
        {loading ? (
          <DashboardStatsSkeleton count={5} />
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3 sm:gap-4">
            <StatCard label="Total"     value={stats.total}     icon={LayoutGrid}   variant="slate"   onClick={() => setFilter('all')}       active={filter === 'all'} />
            <StatCard label="Live"      value={stats.live}      icon={PlayCircle}   variant="emerald" onClick={() => setFilter('live')}      active={filter === 'live'} />
            <StatCard label="Scheduled" value={stats.scheduled} icon={Calendar}     variant="blue"    onClick={() => setFilter('scheduled')} active={filter === 'scheduled'} />
            <StatCard label="Completed" value={stats.completed} icon={CheckCircle2} variant="violet"  onClick={() => setFilter('completed')} active={filter === 'completed'} />
            <StatCard label="Expired"   value={stats.expired}   icon={Clock}        variant="rose"    onClick={() => setFilter('expired')}   active={filter === 'expired'} />
          </div>
        )}

        {/* Subscription Warning */}
        {!canCreateSession() && (
          <Card padding="md" className="bg-amber-50 border-amber-200">
            <div className="flex items-start gap-3">
              <svg className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                <path
                  fillRule="evenodd"
                  d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z"
                  clipRule="evenodd"
                />
              </svg>
              <div className="flex-1">
                <p className="text-xs sm:text-sm font-semibold text-amber-900">Session Limit Reached</p>
                <p className="text-xs sm:text-sm text-amber-700 mt-1">
                  {organization?.subscriptionTier === 'free'
                    ? 'You have reached your free plan limit of 3 sessions. Upgrade to create more sessions.'
                    : `You have reached your session limit of ${organization?.subscriptionTier === 'pro' ? '20 sessions/month' : '3 sessions'}. Upgrade your plan to create more.`
                  }
                </p>
                <div className="mt-3 flex items-center gap-3">
                  <Button variant="ghost" size="sm" onClick={() => navigate('/client/subscription')}>
                    Upgrade Plan
                  </Button>
                </div>
              </div>
            </div>
          </Card>
        )}

        {/* Sessions Table Card */}
        {loading ? (
          <SessionListSkeleton count={5} />
        ) : (
          <div className="bg-white rounded-2xl border border-neutral-100 overflow-hidden">
            {/* Search and Filters inside card */}
            <div className="p-3 sm:p-5 border-b border-neutral-100">
              <div className="flex flex-col sm:flex-row gap-3 items-start sm:items-center justify-between">
                <div className="relative flex-1 w-full sm:max-w-md">
                  <svg
                    className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-neutral-400"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                  </svg>
                  <input
                    type="text"
                    placeholder="Search by name, code, or trainer..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="w-full pl-10 pr-10 py-2 text-sm border border-neutral-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500/20 focus:border-primary-500 transition-all"
                  />
                  {searchQuery && (
                    <button
                      onClick={() => setSearchQuery('')}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-neutral-400 hover:text-neutral-600"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </button>
                  )}
                </div>
                <div className="flex items-center gap-3 w-full sm:w-auto">
                  <select
                    value={filter}
                    onChange={(e) => setFilter(e.target.value)}
                    className="flex-1 sm:flex-none px-3 py-2 text-sm border border-neutral-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500/20 focus:border-primary-500 bg-white appearance-none pr-8 cursor-pointer"
                    style={{ backgroundImage: `url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 20 20'%3e%3cpath stroke='%236b7280' stroke-linecap='round' stroke-linejoin='round' stroke-width='1.5' d='M6 8l4 4 4-4'/%3e%3c/svg%3e")`, backgroundPosition: 'right 0.5rem center', backgroundRepeat: 'no-repeat', backgroundSize: '1.25em 1.25em' }}
                  >
                    <option value="all">All Status</option>
                    <option value="live">Live</option>
                    <option value="scheduled">Scheduled</option>
                    <option value="completed">Completed</option>
                    <option value="expired">Expired</option>
                  </select>
                  <select
                    value={timePeriod}
                    onChange={(e) => setTimePeriod(e.target.value)}
                    className="flex-1 sm:flex-none px-3 py-2 text-sm border border-neutral-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500/20 focus:border-primary-500 bg-white appearance-none pr-8 cursor-pointer"
                    style={{ backgroundImage: `url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 20 20'%3e%3cpath stroke='%236b7280' stroke-linecap='round' stroke-linejoin='round' stroke-width='1.5' d='M6 8l4 4 4-4'/%3e%3c/svg%3e")`, backgroundPosition: 'right 0.5rem center', backgroundRepeat: 'no-repeat', backgroundSize: '1.25em 1.25em' }}
                  >
                    <option value="all">All time</option>
                    <option value="today">Today</option>
                    <option value="week">This week</option>
                    <option value="month">This month</option>
                  </select>
                </div>
              </div>
            </div>

            {/* Sessions List */}
            {filteredSessions.length === 0 ? (
              <div className="p-4">
                {filter === 'all' ? (
                  <NoSessionsEmpty onCreateSession={canCreateSession() ? handleCreateSession : null} />
                ) : (
                  <NoResultsEmpty onClearFilters={() => setFilter('all')} />
                )}
              </div>
            ) : (
              <>
                <ResponsiveDataTable
                  columns={[
                    {
                      key: 'session',
                      label: 'Session',
                      card: 'header',
                      render: (s) => (
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 bg-primary-50 rounded-full flex items-center justify-center text-primary-600 font-semibold text-sm flex-shrink-0">
                            {s.name.charAt(0)}
                          </div>
                          <div>
                            <p className="text-sm font-medium text-neutral-900">{s.name}</p>
                            <p className="text-xs text-neutral-400">Code: {s.code}</p>
                          </div>
                        </div>
                      ),
                    },
                    {
                      key: 'trainer',
                      label: 'Trainer',
                      card: 'body',
                      render: (s) => <span className="text-sm text-neutral-700">{s.trainerName}</span>,
                    },
                    {
                      key: 'datetime',
                      label: 'Date & Time',
                      card: 'body',
                      render: (s) => (
                        <div>
                          <p className="text-sm text-neutral-900">{s.startTime ? formatDateWithTimezone(s.startTime) : 'Not set'}</p>
                          <p className="text-xs text-neutral-400">{formatTime(s.startTime)} - {formatTime(s.endTime)}</p>
                        </div>
                      ),
                    },
                    {
                      key: 'participants',
                      label: 'Participants',
                      card: 'body',
                      render: (s) => <span className="text-sm text-neutral-700">{s.currentParticipants}/{s.expectedParticipants}</span>,
                    },
                    {
                      key: 'status',
                      label: 'Status',
                      card: 'body',
                      render: (s) => <Badge variant={s.status} dot={s.status === 'live'}>{s.status}</Badge>,
                    },
                    {
                      key: 'actions',
                      label: '',
                      card: 'footer',
                      align: 'right',
                      stopPropagation: true,
                      render: (s) => (
                        <div className="flex items-center justify-end gap-1">
                          {s.status === 'live' && (
                            <button onClick={() => navigate(`/client/sessions/${s.id}/monitor`)} className="p-2 rounded-lg text-emerald-500 hover:text-emerald-700 hover:bg-emerald-50 transition-colors" title="Monitor">
                              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" /></svg>
                            </button>
                          )}
                          {s.status === 'scheduled' && (
                            <>
                              <button onClick={() => navigate(`/client/sessions/${s.id}/edit`)} className="p-2 rounded-lg text-neutral-400 hover:text-neutral-700 hover:bg-neutral-100 transition-colors" title="Edit">
                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" /></svg>
                              </button>
                              <button onClick={() => handleDuplicateSession(s)} className="p-2 rounded-lg text-neutral-400 hover:text-neutral-700 hover:bg-neutral-100 transition-colors" title="Duplicate">
                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" /></svg>
                              </button>
                              <button onClick={() => handleDeleteSession(s)} className="p-2 rounded-lg text-red-400 hover:text-red-600 hover:bg-red-50 transition-colors" title="Delete">
                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" /></svg>
                              </button>
                            </>
                          )}
                          {s.status === 'completed' && (
                            <>
                              <button onClick={() => navigate(`/client/sessions/${s.id}/report`)} className="p-2 rounded-lg text-neutral-400 hover:text-neutral-700 hover:bg-neutral-100 transition-colors" title="View Report">
                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 17v-2m3 2v-4m3 4v-6m2 10H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" /></svg>
                              </button>
                              <button onClick={() => handleDuplicateSession(s)} className="p-2 rounded-lg text-neutral-400 hover:text-neutral-700 hover:bg-neutral-100 transition-colors" title="Duplicate">
                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" /></svg>
                              </button>
                            </>
                          )}
                          {s.status === 'expired' && (
                            <>
                              <button
                                onClick={() => setRescheduleModal({ isOpen: true, session: { ...s, _id: s.id || s._id } })}
                                className="p-2 rounded-lg text-primary-600 hover:text-primary-700 hover:bg-primary-50 transition-colors ring-1 ring-inset ring-primary-200/70"
                                title="Reschedule"
                              >
                                <CalendarClock size={16} strokeWidth={1.75} />
                              </button>
                              <button onClick={() => handleDuplicateSession(s)} className="p-2 rounded-lg text-neutral-400 hover:text-neutral-700 hover:bg-neutral-100 transition-colors" title="Duplicate">
                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" /></svg>
                              </button>
                              <button onClick={() => handleDeleteSession(s)} className="p-2 rounded-lg text-red-400 hover:text-red-600 hover:bg-red-50 transition-colors" title="Delete">
                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" /></svg>
                              </button>
                            </>
                          )}
                        </div>
                      ),
                    },
                  ]}
                  data={paginatedSessions}
                  keyExtractor={(s) => s.id}
                  onRowClick={(s) => navigate(`/client/sessions/${s.id}/report`)}
                />
              </>
            )}
            {/* Pagination */}
            {filteredSessions.length > 0 && (
              <div className="px-3 sm:px-5 py-3 sm:py-3.5 border-t border-neutral-100">
                <Pagination
                  currentPage={currentPage}
                  totalPages={totalPages}
                  onPageChange={setCurrentPage}
                  itemsPerPage={itemsPerPage}
                  totalItems={filteredSessions.length}
                />
              </div>
            )}
          </div>
        )}
      </div>

      {/* Delete Confirmation Modal */}
      <Modal
        isOpen={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        title="Delete Session?"
        footer={
          <div className="flex justify-end gap-3">
            <Button variant="ghost" onClick={() => setShowDeleteModal(false)}>
              Cancel
            </Button>
            <Button variant="danger" onClick={confirmDelete}>
              Delete Session
            </Button>
          </div>
        }
      >
        <div className="space-y-4">
          <p className="text-neutral-700">
            Are you sure you want to delete <strong>"{sessionToDelete?.name}"</strong>?
          </p>
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
            <p className="text-xs sm:text-sm text-amber-800">
              <strong>Warning:</strong> This action cannot be undone. All sub-sessions, activities, and participant data will be
              permanently deleted.
            </p>
          </div>
          {sessionToDelete?.subSessions && sessionToDelete.subSessions.length > 0 && (
            <div>
              <p className="text-xs sm:text-sm font-semibold text-neutral-900 mb-2">This will delete:</p>
              <ul className="text-xs sm:text-sm text-neutral-700 space-y-1">
                <li>• {sessionToDelete.subSessions.length} sub-sessions</li>
                <li>• All activities and responses</li>
                <li>• {sessionToDelete.workbooks?.length || 0} uploaded workbooks</li>
              </ul>
            </div>
          )}
        </div>
      </Modal>

      {/* Reschedule modal — opens for expired sessions. On success the page
          refetches so the row moves out of the Expired bucket. */}
      <RescheduleSessionModal
        isOpen={rescheduleModal.isOpen}
        onClose={() => setRescheduleModal({ isOpen: false, session: null })}
        session={rescheduleModal.session}
        onSuccess={() => fetchSessionsData()}
      />
    </DashboardLayout>
  );
};

export default SessionsPage;
